import React from "react";
import { Box, Typography, Switch, Divider, Avatar } from "@mui/material";
import { PhoneInTalkOutlined } from "@mui/icons-material";

import { CardLayout } from "./CardLayout";

/**
 * A card component that displays profile information and active status.
 *
 * @param {Object} props Component props.
 * @param {string} props.profileImage The URL of the profile image.
 * @param {string} props.name The name to display.
 * @param {string} props.subtitle The subtitle to display.
 * @param {string} props.phone The phone number to display.
 * @param {boolean} props.isActive Determines if the account is active.
 * @param {boolean} props.canToggle Determines if the toggle button is visible.
 * @param {boolean} props.canEdit Determines if the account can be edited.
 * @param {boolean} props.canDelete Determines if the account can be deleted.
 * @param {Function} props.onEdit Called when the edit action is clicked.
 * @param {Function} props.onDelete Called when the delete action is clicked.
 * @param {Function} props.onToggle Called when the toggle action is clicked.
 * @param {Array} props.infoFields An array of objects with label and value properties.
 * @returns {ReactElement} The rendered component.
 */

export const ProfileDetails = ({
  profileImage,
  name = "",
  subtitle = "",
  phone = "",
  isActive = false,
  canToggle = false,
  canEdit = false,
  canDelete = false,
  onEdit = () => {},
  onDelete = () => {},
  onToggle = () => {},
  infoFields = [],
}) => {
  return (
    <CardLayout>
      <Box display="flex" width="100%" flexWrap="wrap" px={3}>
        <Box width={{ xs: "100%", md: "50%" }} display="flex" borderRight="1px solid #e0e0e0" pr={3}>
          <Box display="flex" width="100%" alignItems="stretch">
            <Box mr={2} sx={{ width: 180, height: "100%", display: "flex", alignItems: "center" }}>
              <Avatar
                src={profileImage}
                alt="Profile Picture"
                sx={{
                  width: 180,
                  height: 180,
                  borderRadius: "50%",
                  objectFit: "cover",
                  flexShrink: 0,
                  backgroundColor: "#F2ECF9",
                }}
              />
            </Box>
            <Box flex={1} display="flex" flexDirection="column" justifyContent="center" gap={1}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {name}
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 500 }}>
                  {subtitle}
                </Typography>
                <Box display="flex" alignItems="center" gap={1} mt={1}>
                  <PhoneInTalkOutlined fontSize="small" sx={{ fontWeight: 500 }} />
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {phone}
                  </Typography>
                </Box>
              </Box>

              <Box>
                <Divider sx={{ my: 0.5, borderBottomWidth: 2 }} />
                <Box display="flex" alignItems="center" justifyContent={"space-between"} gap={1}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Active
                  </Typography>
                  <Switch checked={isActive} onChange={onToggle} disabled={!canToggle} size="small" />
                </Box>
                <Divider sx={{ my: 0.5, borderBottomWidth: 2 }} />

                {(canEdit || canDelete) && (
                  <Box display="flex" gap={2} alignItems="center">
                    {canEdit && (
                      <Typography
                        variant="body2"
                        sx={{ cursor: "pointer", color: "primary.main", fontWeight: 500 }}
                        onClick={onEdit}
                      >
                        Edit
                      </Typography>
                    )}
                    {canEdit && canDelete && <Divider orientation="vertical" flexItem sx={{ borderRightWidth: 2 }} />}
                    {canDelete && (
                      <Typography
                        variant="body2"
                        sx={{ cursor: "pointer", color: "error.main", fontWeight: 500 }}
                        onClick={onDelete}
                      >
                        Delete
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Box>

        <Box
          width={{ xs: "100%", md: "50%" }}
          pl={3}
          mt={{ xs: 2, md: 0 }}
          display="flex"
          flexDirection="column"
          gap={1}
          justifyContent="center"
        >
          {infoFields.map((field, index) => (
            <>
              <Box key={index} display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 500 }}>
                  {field.label}
                </Typography>
                <Typography
                  variant="body2"
                  align="right"
                  sx={{
                    fontWeight: 500,
                    color: field.color || "text.primary",
                  }}
                >
                  {field.value}
                </Typography>
              </Box>
              <Divider sx={{ borderBottomWidth: 2 }} />
            </>
          ))}
        </Box>
      </Box>
    </CardLayout>
  );
};
